import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Layers, Calculator, ShoppingCart, Truck, Users, Package, BarChart3, Settings, Shield, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';

const MODULE_ICONS = {
  core: Shield, accounting: Calculator, sales: ShoppingCart,
  procurement: Truck, workforce: Users, inventory: Package,
  analytics: BarChart3, operations: Settings,
};

export default function ModulesPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => { fetchModules(); }, []);

  const fetchModules = async () => {
    try { const res = await api.get('/v1/modules'); setModules(res.data); }
    catch { toast.error('Failed to load modules'); }
    finally { setLoading(false); }
  };

  const toggleModule = async (moduleId, currentEnabled) => {
    setToggling(moduleId);
    try {
      const res = await api.put(`/v1/modules/${moduleId}/toggle`, { enabled: !currentEnabled });
      toast.success(`Module ${!currentEnabled ? 'enabled' : 'disabled'}`);
      // Update tenant modules in auth store so sidebar reflects change immediately
      if (res.data?.active_modules) {
        const { setTenantModules } = useAuthStore.getState();
        setTenantModules(res.data.active_modules);
      }
      fetchModules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle module'); }
    finally { setToggling(null); }
  };

  if (loading) return (
    <DashboardLayout><div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div></DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="modules-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

                <Layers size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="modules-title"></h1>

                <p className="text-sm text-muted-foreground mt-0.5">System modules overview</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Enable or disable modules for your organization. Each module provides independent API endpoints.
            </p>
          </div>
          <a href="/api/docs" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="api-docs-btn">
              <ExternalLink size={14} className="mr-1" /> API Docs
            </Button>
          </a>
        </div>

        {/* Module Grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          {modules.map(mod => {
            const Icon = MODULE_ICONS[mod.id] || Settings;
            return (
              <Card key={mod.id} className={`transition-all ${mod.enabled ? 'border-border' : 'border-dashed border-border opacity-60'}`} data-testid={`module-card-${mod.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${mod.enabled ? 'bg-orange-100' : 'bg-muted'}`}>
                        <Icon size={20} className={mod.enabled ? 'text-orange-600' : 'text-muted-foreground'} />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{mod.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{mod.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]">
                            /api/v1/{mod.id}/
                          </Badge>
                          {mod.required && (
                            <Badge className="text-[10px] bg-info/20 text-info">Required</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={mod.enabled}
                      disabled={mod.required || toggling === mod.id}
                      onCheckedChange={() => toggleModule(mod.id, mod.enabled)}
                      data-testid={`module-toggle-${mod.id}`}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* API Info */}
        <Card className="border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50">
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-2">Module API Structure</h3>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <div className="bg-white/80 rounded-lg p-3 font-mono text-xs space-y-1">
                <p className="text-muted-foreground"># Accounting</p>
                <p>GET /api/v1/accounting/accounting/accounts</p>
                <p>GET /api/v1/accounting/accounting/journal-entries</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3 font-mono text-xs space-y-1">
                <p className="text-muted-foreground"># Sales & POS</p>
                <p>GET /api/v1/sales/sales</p>
                <p>POST /api/v1/sales/sales</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3 font-mono text-xs space-y-1">
                <p className="text-muted-foreground"># Procurement</p>
                <p>GET /api/v1/procurement/suppliers</p>
                <p>GET /api/v1/procurement/expenses</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3 font-mono text-xs space-y-1">
                <p className="text-muted-foreground"># Workforce & HR</p>
                <p>GET /api/v1/workforce/employees</p>
                <p>GET /api/v1/workforce/shifts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
